import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { UserAccessRequestsService } from './user-access-requests.service';
import { AppService } from '../../app.service';
import { ApiRequestService } from '../../shared/api-request.service';
import { UtilsService } from '../../shared/utils.service';
import {
  GlobalAccountsSelectorComponent
} from '../../shared/global-accounts-selector/global-accounts-selector.component';
import {
  AccountAccessReasonDialogComponent
} from './account-access-reason-dialog/account-access-reason-dialog.component';

/**
 * The requesting user's view of their own membership requests (SC-301).
 *
 * A dedicated route rather than a blocking dialog. The existing employer prompt uses a dialog with
 * disableClose, which suits a question that must be answered once. This state is different: the user may
 * sit in it for days across several sessions while somebody else decides, and an undismissable modal with
 * nothing behind it would trap them in the product with no way to use the parts they still have.
 *
 * No feature permission. This is a personal surface, so it is gated by authentication alone via the parent
 * user route.
 */
@Component({
  selector: 'app-account-access',
  templateUrl: './account-access.component.html',
  styleUrls: ['./account-access.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false
})
export class AccountAccessComponent implements OnInit, OnDestroy {
  /**
   * Mirrors AccountAccessRequestService::MAX_REQUESTS_PER_ACCOUNT on the API.
   *
   * Duplicated deliberately and used for wording only. The API remains the authority on whether a request
   * is allowed, which it reports per row as can_request_again, so if these two ever drift the button stays
   * correct and only the "last attempt" warning is wrong.
   */
  private static readonly MAX_REQUESTS_PER_ACCOUNT: number = 3;

  requests: any[] = [];

  /**
   * A list rather than one card per request. With several outstanding requests the card layout repeated the
   * same explanation and the same pair of page-level buttons for every row, which buried the only things
   * that actually differ: which company, when, and what state it is in.
   */
  displayedColumns: string[] = ['account_name', 'requested_at', 'status', 'actions'];

  inProgress: boolean = false;

  private requestsSubscription?: Subscription;

  constructor(
    public app: AppService,
    public utils: UtilsService,
    private api: ApiRequestService,
    private router: Router,
    private userAccessRequests: UserAccessRequestsService
  ) {}

  ngOnInit(): void {
    // Read through the shared service rather than fetching independently, so this page, the shell banner
    // and the Account Access entry in the account menu always agree. Refreshing here after an action then
    // updates all three at once, which is why cancelling a request also removes the menu entry.
    this.requestsSubscription = this.userAccessRequests.requests.subscribe(
      (requests: any[]): void => {
        this.requests = requests || [];
      }
    );
    this.getRequests();
  }

  ngOnDestroy(): void {
    this.requestsSubscription?.unsubscribe();
  }

  /**
   * Reloads the user's outstanding and refused requests.
   */
  getRequests(): void {
    this.inProgress = true;
    this.userAccessRequests.refresh();
    // The service swallows failures on purpose, since the banner and menu must not raise errors on every
    // page load. A short settle is enough to clear the spinner here.
    setTimeout((): boolean => (this.inProgress = false), 600);
  }

  /**
   * Withdraws a pending request.
   */
  onCancel(request: any): void {
    this.utils.showModal(
      'Cancel Request',
      '<p>Cancel your request to join <b>' + request.account_name + '</b>?</p>' +
      '<p>You can request to join again afterwards.</p>',
      (): void => {
        this.api.makeRequest('delete', `v2/user/account-access-requests/${request.id}`)
          .then((): void => {
            this.utils.showToast('Your request was cancelled.');
            this.getRequests();
          })
          .catch((error): void => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

  /**
   * Re-raises a request that was declined, letting the user say why.
   *
   * Only offered when the API says it would be accepted (can_request_again), so the user is never shown a
   * button that is guaranteed to fail.
   *
   * The dialog is the substance of the action rather than a confirmation step. Asking again used to resend
   * a byte-for-byte copy of the request that had just been turned down, so the reviewer got a second row
   * with nothing new in it and the likely outcome was the same refusal. A message is the only thing that
   * can change the answer, and attempts are capped, so spending one without being able to say anything was
   * close to pointless.
   */
  onRequestAgain(request: any): void {
    this.utils.showComponentDialog(
      AccountAccessReasonDialogComponent,
      {
        accountName: request.account_name,
        // What they are answering, and what they said last time, so both are in front of them while they
        // write instead of behind a page they have to leave.
        declineReason: request.decline_reason,
        previousReason: request.request_reason,
        attemptsRemaining: this.getAttemptsRemaining(request)
      },
      { width: '600px' }
    ).then((response): void => {
      // Undefined means the dialog was dismissed. An empty reason is a deliberate choice and still sends.
      if ( !response ) {
        return;
      }

      this.inProgress = true;
      this.api.makeRequest('put', 'v2/user/employer', {
        account_id: request.account_id,
        search: null,
        reason: response.reason || null
      })
        .then((subResponse: any): void => {
          this.utils.showToast(
            subResponse?.message || 'Your request has been sent for approval.'
          );
          this.getRequests();
        })
        .catch((error): void => {
          this.utils.handleAPIErrors(error);
        })
        .finally((): boolean => (this.inProgress = false));
    });
  }

  /**
   * How many more times this company may be asked.
   *
   * Derived from decline_count against the API's cap rather than sent as its own field, because
   * can_request_again already answers the only question that gates the button. This is presentation: it
   * exists so the dialog can warn somebody that they are about to spend their final attempt, which is
   * information that is worthless once they have already spent it.
   */
  private getAttemptsRemaining(request: any): number {
    const used: number = Number(request?.decline_count || 0);
    return Math.max(AccountAccessComponent.MAX_REQUESTS_PER_ACCOUNT - used, 0);
  }

  /**
   * Lets the user pick a different employer, reusing the existing selector so this page does not invent a
   * second way to do something the product already does.
   */
  onChooseDifferentEmployer(): void {
    this.utils.showComponentDialog(
      GlobalAccountsSelectorComponent,
      {
        selected: [],
        multiple: false,
        search: ''
      },
      { width: '1024px' },
      (response: number | string): void => {
        if ( typeof response == 'undefined' ) {
          return;
        }

        this.api.makeRequest('put', 'v2/user/employer', {
          account_id: typeof response === 'number' ? response : null,
          search: typeof response !== 'number' ? response : null
        })
          .then((subResponse: any): void => {
            if ( subResponse?.pending_approval ) {
              this.utils.showToast(
                subResponse.message || 'Your request has been sent for approval.'
              );
              this.getRequests();
              return;
            }

            // A brand new account belongs to the user who created it, so there is nothing to approve and
            // they can get straight to work.
            this.utils.showToast('Your employer was successfully changed.');
            this.router.navigate(['/dashboard']);
          })
          .catch((error): void => {
            this.utils.handleAPIErrors(error);
          });
      }
    );
  }

}
